import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TextField from '@mui/material/TextField';
import "./CreateDischargeSummary.scss";
import DateFnsUtils from "@date-io/date-fns";
import moment from "moment";
import Toast from "../../../Toast";
import {
  createDischargeSummary,
  getAllDischargesSummary,
  updateDischargedSummary,
  getTestLatest,
} from "../../../../core/services/api";
import "./CreateDischargeSummary.scss";

// Constants
const VIEW_TYPES = {
  MINIMALIST: "MINIMALIST",
  DETAILED: "DETAILED",
} as const;

const dropdownTest = [
  "MRI",
  "CT Scan",
  "Colonoscopy",
  "Biopsy",
  "Endoscopy",
  "DRE",
  "UltraSound",
] as const;

// Interfaces
interface PatientInfo {
  id: string;
  centerId: string;
  name: string;
}

interface DischargeSummaryForm {
  centerId: string;
  appointmentId: string;
  patientId: string;
  consultantName: string;
  lastEvaluationDetails: string;
  toxicityInLastCycle: string;
  courseDuringStay: string;
  nextCycle: string;
  nextVisitDate: string;
  dateOfAdmission: string;
  dateOfDischarge: string;
  admissionTimeIn: string;
  dischargeTimeIn: string;
  testsRequested: Array<typeof dropdownTest[number]>;
  investigationComments: string;
  disclaimer1: boolean;
  disclaimer2: boolean;
  viewType: typeof VIEW_TYPES[keyof typeof VIEW_TYPES];
}

interface ToastMessage {
  text: string;
  type: "success" | "error" | "warning" | "info";
}

interface DischargeSummaryProps {
  patientInfo: PatientInfo;
  vitals: any[];
  displayToast: ToastMessage | null;
  toggleDisplayToast: (message: ToastMessage | null) => void;
  options: typeof dropdownTest;
  appointmentId: string;
  readOnly: boolean;
  handlePreviewShow: (show: boolean) => void;
}

interface PatientDetailsProps {
  patientInfo: PatientInfo;
  appointmentId: string;
  readOnly: boolean;
  vitalsData: any;
  smartSummaryData: any;
  handlePreviewShow: (show: boolean) => void;
}

// Initial state
const initialState: DischargeSummaryForm = {
  centerId: "",
  appointmentId: "",
  patientId: "",
  consultantName: "",
  lastEvaluationDetails: "",
  toxicityInLastCycle: "",
  courseDuringStay: "",
  nextCycle: "",
  nextVisitDate: "",
  dateOfAdmission: "",
  dateOfDischarge: "",
  admissionTimeIn: "",
  dischargeTimeIn: "",
  testsRequested: [],
  investigationComments: "",
  disclaimer1: false,
  disclaimer2: false,
  viewType: VIEW_TYPES.MINIMALIST,
};

export const CreateDischargeSummary: React.FC<DischargeSummaryProps> = ({
  patientInfo,
  vitals,
  displayToast,
  toggleDisplayToast,
  options,
  appointmentId,
  readOnly,
  handlePreviewShow,
}) => {
  const [formData, setFormData] = useState<DischargeSummaryForm>(initialState);
  const [allOrders, setAllOrders] = useState<any[]>([]);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    const todayDate = moment().local().format("yyyy-MM-DD");
    setFormData((prev) => ({
      ...prev,
      dateOfAdmission: todayDate,
      dateOfDischarge: todayDate,
    }));
  }, []);

  useEffect(() => {
    if (appointmentId) {
      getAllDischargesSummary(appointmentId)
        .then((res) => {
          setAllOrders(res);
          if (res?.length > 0) {
            setFormData(res?.[0]);
          }
        })
        .catch((err) => {
          console.error("Error fetching discharge summaries:", err);
          toggleDisplayToast({
            text: "Failed to fetch discharge summaries",
            type: "error",
          });
        })
    }
  }, [appointmentId, isSaved]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleDateChange = (field: keyof DischargeSummaryForm, value: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value ? moment(value).format("YYYY-MM-DD") : "",
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.dateOfAdmission || !formData.dateOfDischarge) {
      toggleDisplayToast({
        text: "Please select both admission and discharge dates",
        type: "error",
      });
      return false;
    }

    if (!formData.dischargeTimeIn) {
      toggleDisplayToast({
        text: "Please select time out",
        type: "error",
      });
      return false;
    }

    if (formData.dischargeTimeIn < formData.admissionTimeIn) {
      toggleDisplayToast({
        text: "Time out cannot be lesser than Time in",
        type: "error",
      });
      return false;
    }

    if (!formData.disclaimer1 || !formData.disclaimer2) {
      toggleDisplayToast({
        text: "Please approve the disclaimers before proceeding further",
        type: "error",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setIsSaving(true)
    if (!validateForm()) {
      setIsSaving(false)
      return;
    }

    const submissionData = {
      ...formData,
      centerId: patientInfo.centerId,
      patientId: patientInfo.id,
      appointmentId,
      dateOfAdmission: moment(formData.dateOfAdmission).format("YYYY-MM-DD"),
      dateOfDischarge: moment(formData.dateOfDischarge).format("YYYY-MM-DD"),
    };

    try {
      const isUpdate = allOrders.length > 0;
      const response = isUpdate
        ? await updateDischargedSummary(allOrders[0]?.id, submissionData)
        : await createDischargeSummary(submissionData);

      if (response) {
        setIsSaved(!isSaved)
        toggleDisplayToast({
          text: `Discharge Summary ${isUpdate ? "Updated" : "Created"} Successfully`,
          type: "success",
        });
      }
    } catch (error: any) {
      console.error("Error saving discharge summary:", error);
      toggleDisplayToast({
        text: error?.response?.data?.message || "Failed to save discharge summary",
        type: "error",
      });
    }
    finally {
      setIsSaving(false)
    }
  };

  return (
    <div className="add-new-wrapper-discharge">
      <div className="admission-row">
        <div className="date-wrapper-mui">
          <LocalizationProvider dateAdapter={AdapterDateFns} utils={DateFnsUtils}>
            <label>Date of Admission *</label>
            <DatePicker
              maxDate={new Date()}
              inputFormat="dd/MM/yyyy"
              value={formData.dateOfAdmission || null}
              onChange={(value) => handleDateChange("dateOfAdmission", value)}
              disabled={readOnly}
              renderInput={(params) => (
                <TextField {...params} variant="standard" helperText={!formData.dateOfAdmission ? "Required" : null} error={!formData.dateOfAdmission} id="admission-date-picker"
                  className="date-picker"
                  style={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    marginTop: "15px",
                  }} />
              )}
            />
          </LocalizationProvider>
        </div>

        <div className="time-input-container">
          <label>Time In</label>
          <input
            type="time"
            className="custom-input"
            name="admissionTimeIn"
            value={formData.admissionTimeIn}
            onChange={handleInputChange}
            disabled={readOnly}
          />
        </div>

        <div className="date-wrapper-mui">
          <LocalizationProvider dateAdapter={AdapterDateFns} utils={DateFnsUtils}>
            <label>Date of Discharge *</label>
            <DatePicker
              minDate={new Date(formData.dateOfAdmission) || undefined}
              maxDate={new Date()}
              inputFormat="dd/MM/yyyy"
              value={formData.dateOfDischarge || null}
              onChange={(value) => handleDateChange("dateOfDischarge", value)}
              disabled={readOnly}
              renderInput={(params) => (
                <TextField {...params} variant="standard"
                  error={!formData.dateOfDischarge}
                  helperText={!formData.dateOfDischarge ? "Required" : null}
                  id="discharge-date-picker"
                  className="date-picker"
                  style={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    marginTop: "15px",
                  }} />
              )}
            />
          </LocalizationProvider>
        </div>

        <div className="time-input-container">
          <label>Time Out *</label>
          <input
            type="time"
            className="custom-input"
            name="dischargeTimeIn"
            value={formData.dischargeTimeIn}
            onChange={handleInputChange}
            disabled={readOnly}
          />
        </div>
      </div>

      <hr />

      <div className="single-discharge-row">
        <label>Course During Hospital Stay</label>
        <textarea
          className="custom-input-course"
          name="courseDuringStay"
          value={formData.courseDuringStay}
          onChange={handleInputChange}
          disabled={readOnly}
        />
      </div>

      <hr />

      <div className="disclaimer-section">
        <label className="disclaimer-detail">Disclaimer</label>
        <div className="disclaimer">
          <label className="disclaimer-detail-label">
            <input
              type="checkbox"
              name="disclaimer1"
              checked={formData.disclaimer1}
              onChange={handleInputChange}
              disabled={readOnly}
            />
            <span className="disclaimer-text">
              The patient and relatives have been explained the side effects and risks
              associated with chemotherapy in the language best understood by them,
              and consent has been taken prior to the administration of chemotherapy.
            </span>
          </label>
          <label className="disclaimer-detail-label">
            <input
              type="checkbox"
              name="disclaimer2"
              checked={formData.disclaimer2}
              onChange={handleInputChange}
              disabled={readOnly}
            />
            <span className="disclaimer-text">
              All the procedures and take-home medication have been explained to the
              patient and the relative in the language best understood by them.
            </span>
          </label>
        </div>
      </div>

      <hr />

      <div className="view-type-section">
        <label className="disclaimer-detail-view">Discharge Summary View:</label>
        <div className="disclaimer-detail-buttons">
          {Object.entries(VIEW_TYPES).map(([key, value]) => (
            <label key={key} className="disclaimer-detail-label">
              <input
                type="radio"
                name="viewType"
                value={value}
                checked={formData.viewType === value}
                onChange={handleInputChange}
                disabled={readOnly}
              />
              <span className="disclaimer-detail-tag">
                {key.charAt(0) + key.slice(1).toLowerCase()}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="button-container">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSaving || readOnly}
          style={{ margin: "25px" }}
        >
          {allOrders?.length > 0 ? "Update" : "Save"} Discharge Summary
        </Button>
      </div>

      {displayToast && (
        <Toast
          textMessage={displayToast.text}
          type={displayToast.type}
          onClose={() => toggleDisplayToast(null)}
          onCloseText="Close"
          onClickText={null}
        />
      )}
    </div>
  );
};

const PatientDetails: React.FC<PatientDetailsProps> = ({
  patientInfo,
  appointmentId,
  readOnly,
  handlePreviewShow,
}) => {
  const [testResults, setTestResults] = useState<any[]>([]);
  const [displayToast, setDisplayToast] = useState<ToastMessage | null>(null);
  const [options] = useState<typeof dropdownTest>(dropdownTest);

  useEffect(() => {
    if (appointmentId) {
      getTestLatest(appointmentId)
        .then((res) => {
          if (res?.vitals != null) {
            setTestResults(res.vitals);
          }
        })
        .catch((err) => {
          console.error("Error fetching test results:", err);
          setDisplayToast({
            text: "Failed to fetch test results",
            type: "error",
          });
        });
    }
  }, [appointmentId]);

  return (
    <CreateDischargeSummary
      patientInfo={patientInfo}
      vitals={testResults}
      displayToast={displayToast}
      toggleDisplayToast={setDisplayToast}
      options={options}
      appointmentId={appointmentId}
      readOnly={readOnly}
      handlePreviewShow={handlePreviewShow}
    />
  );
};

export default PatientDetails;